import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IGuestRating, IListingRating, IReview, ISubPhoto } from '@curbnturf/entities';
import { Observable } from 'rxjs';
import { BASE_API_URL } from '../network/api-config';

const API_URL = BASE_API_URL + 'rating';

/**
 * Local fork of @curbnturf/rating's RatingService — trimmed to plain
 * HttpClient. createBoondockRatings/createPoiRatings/fetchBoondockRatings/
 * fetchPoiRatings aren't used, so aren't ported. The real class's
 * offlineCallback/syncAction options (NgRx offline-sync) are dropped —
 * always a no-op under the old LocalCachedHttpClient override.
 */
@Injectable({ providedIn: 'root' })
export class RatingApiService {
  private readonly http = inject(HttpClient);

  fetchListingRatings(listingId: number): Observable<IListingRating[]> {
    if (!listingId) {
      throw new Error('Unable to fetch ratings. No ID provided');
    }

    return this.http.get<IListingRating[]>(`${API_URL}/listing/${listingId}`);
  }

  /** A fresh random set of up to 10 five-star listing reviews, for the marketing home page. */
  getRandomFiveStarReviews(): Observable<IListingRating[]> {
    return this.http.get<IListingRating[]>(`${API_URL}/listing/reviews/random`);
  }

  fetchGuestRatings(userId: number): Observable<IGuestRating[]> {
    if (!userId) {
      throw new Error('Unable to fetch guest ratings. No ID provided');
    }

    return this.http.get<IGuestRating[]>(`${API_URL}/guest/${userId}`);
  }

  createListingRatings(ratings: IListingRating[]): Observable<IListingRating[]> {
    const saveableRatings = ratings.map((rating) => ({
      ...rating,
      creatorId: rating.creator?.id || rating.creatorId,
      creator: {
        id: rating.creator?.id || rating.creatorId,
      },
    }));
    return this.http.post<IListingRating[]>(`${API_URL}/listing`, saveableRatings);
  }

  reviewListing(listingId: number, review: IReview): Observable<IReview> {
    return this.http.post<IReview>(`${API_URL}/listing/${listingId}/review`, review);
  }

  reviewGuest(guestId: number, review: IReview): Observable<IReview> {
    return this.http.post<IReview>(`${API_URL}/guest/${guestId}/review`, review);
  }

  uploadReviewPhoto(listingId: number, reviewId: number, formData: FormData): Observable<ISubPhoto[]> {
    return this.http.post<ISubPhoto[]>(`${API_URL}/listing/${listingId}/review/${reviewId}/photo`, formData);
  }

  createGuestRatings(ratings: IGuestRating[]): Observable<IGuestRating[]> {
    const saveableRatings = ratings.map((rating) => ({
      ...rating,
      creatorId: rating.creator?.id || rating.creatorId,
      creator: {
        id: rating.creator?.id || rating.creatorId,
      },
    }));
    return this.http.post<IGuestRating[]>(`${API_URL}/guest`, saveableRatings);
  }
}
